import * as React from 'react';
import { Switch } from '@progress/kendo-react-inputs';
import styled from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import FieldDecorator from './FieldDecorator';
import { FieldProps } from '../../core/form';
import { fieldHeight, aboveCommonElementsZIndex, phoneOnlyMaxWidth } from '../../appComponents/cssConstants';
import { DisplayNoneOnPrint } from '../PrintStyles';
import { PrintOnlyValue } from '../PrintOnlyField';
import { hasValue } from '../../lib/lib';

// Use this component in 2 ways:
// 1. Only set onLable and offLabel for boolean field.
// 2. Set string type onValue and offValue for string field.
const SwitchField = ({
	label,
	name,
	disabled,
	onLabel = 'Yes',
	offLabel = 'No',
	onValue,
	offValue,
	invertValue = false,
	fullWidthInDesktop = true,
	subLabel,
	className = ''
}: Props) => {
	const {
		formState: { errors }
	} = useFormContext();
	const { field } = useController({ name });
	const { value } = field;
	const checked = isChecked(invertValue, value, onValue, offValue);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChange = (e: any) => {
		const newChecked = isChecked(invertValue, e.target.value);
		let newValue;
		if (hasValue(onValue) && hasValue(offValue)) newValue = newChecked ? onValue : offValue;
		else newValue = newChecked;
		field.onChange(newValue);
	};

	return (
		<FieldWrapper className={className} name={name} errors={errors} label={label} subLabel={subLabel}>
			<StyledSwitch
				name={name}
				id={name}
				onChange={handleChange}
				checked={checked}
				onLabel={onLabel}
				offLabel={offLabel}
				disabled={disabled}
				$fullWidthInDesktop={fullWidthInDesktop}
			/>
			<PrintOnlyValue>{checked ? offLabel : onLabel}</PrintOnlyValue>
		</FieldWrapper>
	);
};

interface Props extends FieldProps {
	onLabel?: string;
	offLabel?: string;
	onValue?: string;
	offValue?: string;
	invertValue?: boolean;
	fullWidthInDesktop?: boolean;
	subLabel?: string | null;
	className?: string;
}

export default SwitchField;

const isChecked = (
	invertValue: boolean,
	fieldValue: string | boolean,
	onValue?: string | null,
	offValue?: string | null
) => {
	if (hasValue(onValue) && hasValue(offValue)) {
		return invertValue ? fieldValue === offValue : fieldValue === onValue;
	}

	return invertValue ? !fieldValue : (fieldValue as boolean);
};

const FieldWrapper = styled(FieldDecorator)`
	vertical-align: bottom;
	text-align: left;
`;

const StyledSwitch = styled(Switch)<{ $fullWidthInDesktop: boolean }>`
	width: ${(props) => (props.$fullWidthInDesktop ? '100%' : '50%')};
	height: ${fieldHeight};
	align-items: stretch;
	overflow: hidden;

	${DisplayNoneOnPrint}

	@media (max-width: ${phoneOnlyMaxWidth}) {
		width: 100%;
	}

	.k-switch-track {
		height: ${fieldHeight};
		outline: none !important;
		background-color: white;
		border-color: rgba(0, 0, 0, 0.08);
	}

	.k-switch-thumb-wrap {
		width: 100%;
		height: 100%;
		top: 0;
	}

	&.k-switch-off .k-switch-thumb-wrap {
		left: 0;
	}

	&.k-switch-on .k-switch-thumb-wrap {
		left: 50%;
	}

	.k-switch-thumb {
		width: 50%;
		height: 100%;
		background-color: ${(props) => props.theme.brandingColourHeavy};
		transform: none;
	}

	.k-switch-label-on,
	.k-switch-label-off {
		z-index: ${aboveCommonElementsZIndex};
		color: ${(props) => props.theme.brandingColourHeavy};
		font-size: 18px;
		width: 50%;
		text-align: center;
		text-transform: initial;
	}

	&.k-switch-on .k-switch-label-off {
		color: white;
		transition: color 0.5s ease;
	}

	&.k-switch-off .k-switch-label-on {
		color: white;
		transition: color 0.5s ease;
	}
`;
